import React, { useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Chip, Grid, Avatar,
  Tab, Tabs, TextField, MenuItem, Divider, IconButton, Tooltip,
  CircularProgress, Alert, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import FlagIcon from '@mui/icons-material/Flag';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChatIcon from '@mui/icons-material/Chat';
import { useQuery, useMutation } from '@apollo/client';
import {
  MATCHES_QUERY, SHORTLISTED_QUERY, SENT_INTERESTS_QUERY, RECEIVED_INTERESTS_QUERY,
  SEND_INTEREST_MUTATION, RESPOND_INTEREST_MUTATION, TOGGLE_SHORTLIST_MUTATION, REPORT_PROFILE_MUTATION,
} from '../graphql/queries';
import { useLovMany } from '../hooks/useLov';
import { useNavigate } from 'react-router-dom';

import Paged from '../components/Paged'; // lists beyond 10 are paged
function InterestChip({ status }) {
  if (!status) return null;
  const map = {
    pending:  { label: 'Pending',  color: 'warning', icon: <HourglassTopIcon fontSize="inherit" /> },
    accepted: { label: 'Accepted', color: 'success', icon: <CheckCircleIcon  fontSize="inherit" /> },
    declined: { label: 'Declined', color: 'error',   icon: <CancelIcon       fontSize="inherit" /> },
  };
  const cfg = map[status] || {};
  return <Chip label={cfg.label} color={cfg.color} size="small" icon={cfg.icon} variant="outlined" />;
}

// Report a profile (MeitY advisory: encourage reporting of fraud); creates a grievance ticket
const REPORT_REASONS = [
  ['fake', 'Fake or misleading profile'], ['fraud', 'Asked for money / suspected fraud'], ['dowry', 'Dowry demand'],
  ['harassment', 'Harassment or abuse'], ['minor', 'Appears to be under the legal age'], ['inappropriate', 'Obscene or inappropriate content'], ['other', 'Other'],
];
function ReportDialog({ open, onClose, userId, name }) {
  const [reason, setReason] = useState('fake');
  const [details, setDetails] = useState('');
  const [done, setDone] = useState('');
  const [report, { loading, error }] = useMutation(REPORT_PROFILE_MUTATION, {
    onCompleted: (d) => setDone(d.reportProfile.ticketNumber),
  });
  const close = () => { setDone(''); setDetails(''); onClose(); };
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Report {name}</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success">Thank you. Our Grievance Officer will look into it — ticket <b>{done}</b>. If you are in danger or have lost money, also call 1930 or report at cybercrime.gov.in.</Alert>
        ) : (
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error.message}</Alert>}
            <TextField select label="What is wrong?" value={reason} onChange={e => setReason(e.target.value)} fullWidth>
              {REPORT_REASONS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </TextField>
            <TextField label="Details" value={details} onChange={e => setDetails(e.target.value)} fullWidth multiline minRows={3}
              placeholder="What happened? Include dates and any messages." />
            <Typography variant="caption" color="text.secondary">The member is not told who reported them.</Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && <Button variant="contained" color="error" disabled={loading} onClick={() => report({ variables: { userId, reason, details } })}>Report</Button>}
      </DialogActions>
    </Dialog>
  );
}

function ProfileCard({ p, onSendInterest, onToggleShortlist, onRespond, showRespond, onMessage }) {
  const initials = `${p.firstName?.[0] || '?'}${p.lastName?.[0] || ''}`;
  const age = p.age || (p.dob ? Math.floor((Date.now() - new Date(p.dob)) / (365.25*24*3600*1000)) : '?');
  const score = p.compatibilityScore || 0;
  const [reporting, setReporting] = useState(false);

  return (
    <>
    <ReportDialog open={reporting} onClose={() => setReporting(false)} userId={p.userId || p.id} name={`${p.firstName} ${p.lastName?.[0] || ''}.`} />
    <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
        <Avatar sx={{ bgcolor: p.gender === 'female' ? 'secondary.main' : 'primary.main', width: 56, height: 56, fontSize: '1.2rem' }}>
          {initials}
        </Avatar>
        <Box flex={1}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography fontWeight={700} variant="subtitle1" noWrap>
              {p.firstName} {p.lastName?.[0]}.
            </Typography>
            {p.verified && (
              <Tooltip title="Identity and address documents checked by our team. This is not a guarantee — please do your own checks.">
                <VerifiedIcon color="primary" sx={{ fontSize: 18 }} />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {age} yrs &bull; {p.community || 'N/A'} &bull; {p.city ? `${p.city}${p.state ? ', ' + p.state : ''}` : (p.location || 'N/A')}
          </Typography>
          <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
            {p.horoscopeStar && <Chip label={p.horoscopeStar} size="small" sx={{ fontSize: 10 }} />}
            {p.maritalStatus && <Chip label={p.maritalStatus.replace(/_/g,' ')} size="small" color="default" sx={{ fontSize: 10 }} />}
          </Stack>
        </Box>
        <Tooltip title="Report this profile">
          <IconButton size="small" onClick={() => setReporting(true)}><FlagIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title={p.isShortlisted ? 'Remove shortlist' : 'Shortlist'}>
          <IconButton size="small" onClick={() => onToggleShortlist(p.userId || p.id, p.isShortlisted)} color={p.isShortlisted ? 'error' : 'default'}>
            {p.isShortlisted ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {p.profession && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {p.profession}{p.employmentType ? ` • ${p.employmentType}` : ''}
        </Typography>
      )}
      {p.education && <Typography variant="body2" color="text.secondary" mb={1}>{p.education}</Typography>}

      {score > 0 && (
        <Box mb={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">Compatibility</Typography>
            <Typography variant="caption" fontWeight={700} color={score >= 60 ? 'success.main' : score >= 30 ? 'warning.main' : 'text.secondary'}>
              {score}%
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={score}
            color={score >= 60 ? 'success' : score >= 30 ? 'warning' : 'error'}
            sx={{ height: 5, borderRadius: 3, mt: 0.5 }} />
        </Box>
      )}

      <Box flex={1} />
      <Divider sx={{ my: 1.5 }} />

      {showRespond && p.interestStatus === 'pending' ? (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
            onClick={() => onRespond(p.interestId, 'declined')}>Decline</Button>
          <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
            onClick={() => onRespond(p.interestId, 'accepted')}>Accept</Button>
        </Stack>
      ) : (
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <InterestChip status={p.interestStatus} />
          {!p.interestStatus && (
            <Button size="small" variant="contained" startIcon={<SendIcon />}
              onClick={() => onSendInterest(p.userId || p.id)}>
              Send Interest
            </Button>
          )}
          <Tooltip title="Send a message">
            <IconButton size="small" color="info" onClick={() => onMessage && onMessage(p.userId)}>
              <ChatIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Paper>
    </>
  );
}

export default function MatchmakingPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState({ minAge:'', maxAge:'', community:'', gender:'', religion:'', motherTongue:'', maritalStatus:'', city:'' });
  const [showFilters, setShowFilters] = useState(false);

  const { lovMap } = useLovMany(['age_range', 'community', 'gender', 'religion', 'mother_tongue', 'marital_status', 'city']);
  const ageOpts         = lovMap['age_range']    || [];
  const communityOpts   = [{ value: '', label: 'Any' }, ...(lovMap['community']     || [])];
  const genderOpts      = [{ value: '', label: 'Any' }, ...(lovMap['gender']        || [])];
  const religionOpts    = [{ value: '', label: 'Any' }, ...(lovMap['religion']      || [])];
  const motherTongueOpts= [{ value: '', label: 'Any' }, ...(lovMap['mother_tongue'] || [])];
  const maritalOpts     = [{ value: '', label: 'Any' }, ...(lovMap['marital_status']|| [])];
  const cityOpts        = [{ value: '', label: 'Any City' }, ...(lovMap['city']     || [])];

  const matchVars = {
    gender:        filters.gender       || undefined,
    community:     filters.community === 'Any' ? undefined : (filters.community || undefined),
    religion:      filters.religion     || undefined,
    motherTongue:  filters.motherTongue || undefined,
    maritalStatus: filters.maritalStatus|| undefined,
    minAge:        filters.minAge ? parseInt(filters.minAge) : undefined,
    maxAge:        filters.maxAge ? parseInt(filters.maxAge) : undefined,
    city:          filters.city || undefined,
  };

  const { data: matchData, loading: matchLoading, refetch: refetchMatches } = useQuery(MATCHES_QUERY, { variables: matchVars, skip: tab !== 0 });
  const { data: shortlistData, loading: shortlistLoading, refetch: refetchShortlist } = useQuery(SHORTLISTED_QUERY, { skip: tab !== 1 });
  const { data: sentData, loading: sentLoading } = useQuery(SENT_INTERESTS_QUERY, { skip: tab !== 2 });
  const { data: receivedData, loading: receivedLoading, refetch: refetchReceived } = useQuery(RECEIVED_INTERESTS_QUERY, { skip: tab !== 3 });

  const [sendInterest] = useMutation(SEND_INTEREST_MUTATION, { onCompleted: () => refetchMatches() });
  const [respondInterest] = useMutation(RESPOND_INTEREST_MUTATION, { onCompleted: () => refetchReceived() });
  const [toggleShortlist] = useMutation(TOGGLE_SHORTLIST_MUTATION, {
    onCompleted: () => { refetchMatches(); if (tab === 1) refetchShortlist(); }
  });

  const handleSendInterest    = (toUserId)     => sendInterest({ variables: { toUserId } });
  const handleToggleShortlist = (targetId)     => toggleShortlist({ variables: { targetId } });
  const handleRespond         = (interestId, status) => respondInterest({ variables: { interestId, status } });
  const handleMessage         = (userId)       => navigate(`/dashboard/messages?with=${userId}`);

  const fch = (e) => setFilters(f => ({ ...f, [e.target.name]: e.target.value }));

  const matches   = matchData?.matches          || [];
  const shortlist = shortlistData?.shortlisted  || [];
  const sent      = (sentData?.sentInterests     || []).map(i => ({
    id: i.receiver?.id,
    interestId: i.id,
    firstName: i.receiver?.profile?.firstName || i.receiver?.email?.split('@')[0] || '?',
    lastName: i.receiver?.profile?.lastName || '',
    community: i.receiver?.profile?.community,
    location: i.receiver?.profile?.city
      ? [i.receiver.profile.city, i.receiver.profile.state].filter(Boolean).join(', ')
      : null,
    photoUrl: i.receiver?.profile?.photoUrl,
    interestStatus: i.status,
    isShortlisted: false,
  }));
  const received  = (receivedData?.receivedInterests || []).map(i => ({
    id: i.sender?.id,
    interestId: i.id,
    firstName: i.sender?.profile?.firstName || i.sender?.email?.split('@')[0] || '?',
    lastName: i.sender?.profile?.lastName || '',
    community: i.sender?.profile?.community,
    location: i.sender?.profile?.city
      ? [i.sender.profile.city, i.sender.profile.state].filter(Boolean).join(', ')
      : null,
    photoUrl: i.sender?.profile?.photoUrl,
    interestStatus: i.status,
    isShortlisted: false,
  }));
  const loading   = matchLoading || shortlistLoading || sentLoading || receivedLoading;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Find Matches</Typography>
          <Typography variant="body2" color="text.secondary">
            Browse compatible profiles and connect
          </Typography>
        </Box>
        {tab === 0 && (
          <Button startIcon={<FilterListIcon />} variant="outlined" onClick={() => setShowFilters(f => !f)}>
            {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
        )}
      </Stack>

      {tab === 0 && showFilters && (
        <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <TextField label="Min Age" name="minAge" value={filters.minAge} onChange={fch} fullWidth size="small" select>
                <MenuItem value="">Any</MenuItem>
                {ageOpts.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Max Age" name="maxAge" value={filters.maxAge} onChange={fch} fullWidth size="small" select>
                <MenuItem value="">Any</MenuItem>
                {ageOpts.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Community" name="community" value={filters.community} onChange={fch} fullWidth size="small" select>
                {communityOpts.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Gender" name="gender" value={filters.gender} onChange={fch} fullWidth size="small" select>
                {genderOpts.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Religion" name="religion" value={filters.religion} onChange={fch} fullWidth size="small" select>
                {religionOpts.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Mother Tongue" name="motherTongue" value={filters.motherTongue} onChange={fch} fullWidth size="small" select>
                {motherTongueOpts.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Marital Status" name="maritalStatus" value={filters.maritalStatus} onChange={fch} fullWidth size="small" select>
                {maritalOpts.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="City" name="city" value={filters.city} onChange={fch} fullWidth size="small" select>
                {cityOpts.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button variant="outlined" size="small" fullWidth
                onClick={() => setFilters({ minAge:'', maxAge:'', community:'', gender:'', religion:'', motherTongue:'', maritalStatus:'', city:'' })}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`New Matches${matches.length ? ` (${matches.length})` : ''}`} />
        <Tab label={`Shortlisted${shortlist.length ? ` (${shortlist.length})` : ''}`} />
        <Tab label={`Sent${sent.length ? ` (${sent.length})` : ''}`} />
        <Tab label={`Received${received.length ? ` (${received.length})` : ''}`} />
      </Tabs>

      {loading && (
        <Stack alignItems="center" py={6}><CircularProgress /></Stack>
      )}

      {!loading && tab === 0 && (
        matches.length === 0
          ? <Alert severity="info">No matches found. Try adjusting your filters or complete your profile.</Alert>
          : <Grid container spacing={3}>
              <Paged items={matches}>{rows => rows.map(p => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <ProfileCard p={p} onSendInterest={handleSendInterest} onToggleShortlist={handleToggleShortlist} onRespond={handleRespond} showRespond={false} onMessage={handleMessage} />
                </Grid>
              ))}</Paged>
            </Grid>
      )}

      {!loading && tab === 1 && (
        shortlist.length === 0
          ? <Alert severity="info">No shortlisted profiles yet. Click the heart icon on any profile to shortlist.</Alert>
          : <Grid container spacing={3}>
              <Paged items={shortlist}>{rows => rows.map(p => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <ProfileCard p={{ ...p, isShortlisted: true }} onSendInterest={handleSendInterest} onToggleShortlist={handleToggleShortlist} onRespond={handleRespond} showRespond={false} onMessage={handleMessage} />
                </Grid>
              ))}</Paged>
            </Grid>
      )}

      {!loading && tab === 2 && (
        sent.length === 0
          ? <Alert severity="info">You have not sent any interests yet.</Alert>
          : <Grid container spacing={3}>
              <Paged items={sent}>{rows => rows.map(p => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <ProfileCard p={p} onSendInterest={handleSendInterest} onToggleShortlist={handleToggleShortlist} onRespond={handleRespond} showRespond={false} onMessage={handleMessage} />
                </Grid>
              ))}</Paged>
            </Grid>
      )}

      {!loading && tab === 3 && (
        received.length === 0
          ? <Alert severity="info">No interests received yet. Complete your profile to attract more matches.</Alert>
          : <Grid container spacing={3}>
              <Paged items={received}>{rows => rows.map(p => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <ProfileCard p={p} onSendInterest={handleSendInterest} onToggleShortlist={handleToggleShortlist} onRespond={handleRespond} showRespond={true} onMessage={handleMessage} />
                </Grid>
              ))}</Paged>
            </Grid>
      )}
    </Box>
  );
}
